import { Component, EventEmitter, Input, Output, input } from '@angular/core';

import { ConversationViewOptions } from '../../button-toggle/button-toggle.component';

@Component({
  selector: 'phoenix-conversation-miniature',
  template: `
    <div
      class="h-full w-full theme-bg-1 border-color-1 p-2 rounded-sm mat-elevation-z2 overflow-hidden"
      [ngClass]="{
        'theme-bg-4 border-blue-color-1--2': isSelectedData(),
        'opacity-50 grayscale cursor-not-allowed': isCorrupted,
      }"
      [matTooltip]="isCorrupted ? 'Corrupted information' : ''"
      [id]="data.id"
      (click)="isCorrupted ? $event.stopPropagation() : null"
      (keydown)="(null)"
    >
      <div
        [attr.aria-label]="
          (data?.collectionInfo?.sourceName ?? 'conversation')
            .replace(' ', '')
            .toLocaleLowerCase() + 'ConversationMiniature'
        "
        class="w-full flex flex-wrap items-center overflow-hidden theme-text-2 "
      >
        <div class="flex flex-row items-center flex-none overflow-hidden">
          @if (miniatureFormat) {
            <img
              class="max-w-[24px] max-h-[24px]"
              [src]="
                '/assets/images/icons/social-icons/' +
                (conversationInfo?.type ?? 'DEFAULT') +
                '.png'
              "
              (error)="onImgError($event)"
              [alt]="'Icon for ' + (conversationInfo?.type ?? 'conversation')"
            />
          }
          <div>
            <mat-icon class="material-icons-outlined mx-2 mb-1 pt-1">
              {{ conversationInfo?.is_channel ? 'groups' : 'mail' }}
            </mat-icon>
          </div>
          <span class="font-extrabold mx-2 truncate">
            {{ data?.collectionInfo?.sourceName ?? 'Unknown' }}
          </span>
        </div>

        @if (miniatureFormat) {
          <ng-template [ngTemplateOutlet]="members"></ng-template>
        }

        <div
          [matTooltip]="(conversationInfo?.total ?? 0) + ' messages'"
          class="mx-2 flex justify-center items-center bg-white text-black font-semibold text-sm px-4 py-2 rounded-sm h-8"
        >
          <span>{{ conversationInfo?.total ?? 0 }}</span>
        </div>

        <div class="flex flex-row grow-0 ml-auto">
          @if (conversationInfo?.start_date || data?.updatedAt) {
            <div
              class="flex flex-col text-xs blue-theme-bg-1 blue-theme-text-1 overflow-hidden text-ellipsis font-semibold px-2 py-1 rounded ml-auto justify-center"
            >
              @for (
                eventTime of [conversationInfo?.start_date, data?.updatedAt];
                track eventTime
              ) {
                @if (eventTime) {
                  <span class="truncate">
                    {{ eventTime | date: 'yyyy-MM-ddTHH:mm:ss' : 'UTC' }}
                  </span>
                }
              }
            </div>
          }
          @if (!miniatureFormat) {
            <div class="ml-2 flex items-center justify-center">
              @if (showHighlightButton) {
                <button
                  class="secondary"
                  mat-icon-button
                  [attr.aria-label]="'extra-action-button'"
                  [disabled]="isCorrupted"
                  [matTooltip]="
                    isCorrupted
                      ? 'Corrupted information'
                      : 'Scroll to highlight'
                  "
                  (click)="
                    isCorrupted
                      ? $event.stopPropagation()
                      : handleScrollToHighlight()
                  "
                >
                  <mat-icon> search </mat-icon>
                </button>
              }

              @if (showDownloadButton) {
                <button
                  mat-icon-button
                  class="secondary material-icons-outlined"
                  color="primary"
                  [disabled]="isCorrupted"
                  [matTooltip]="
                    isCorrupted
                      ? 'Corrupted information'
                      : 'Download Conversation'
                  "
                  (click)="
                    isCorrupted ? $event.stopPropagation() : startDownload()
                  "
                >
                  cloud_download
                </button>
              }

              <button
                [matTooltip]="
                  isCorrupted ? 'Corrupted information' : 'Select format'
                "
                mat-icon-button
                [disabled]="isCorrupted"
                [matMenuTriggerFor]="menu"
                (click)="isCorrupted ? $event.stopPropagation() : null"
              >
                <mat-icon>settings</mat-icon>
              </button>

              <div class="mt-2">
                <phoenix-translation-switch
                  [translationsEnabled]="translationsEnabled"
                ></phoenix-translation-switch>
              </div>

              <mat-menu class=" px-2 theme-bg-1  " #menu="matMenu">
                <div class="w-[270px]">
                  <phoenix-button-toggle
                    [options]="options"
                    (valueChange)="toggleSelected($event)"
                  ></phoenix-button-toggle>
                </div>
              </mat-menu>
            </div>
          }
        </div>
      </div>
      @if (!miniatureFormat) {
        <ng-template [ngTemplateOutlet]="members"></ng-template>
      }
    </div>

    <ng-template #members>
      <div
        [matTooltip]="(conversationInfo?.members ?? []).join(', ')"
        class="flex-1 mx-1 truncate overflow-hidden"
      >
        <span class="truncate text-sm theme-text-1">
          {{
            conversationInfo?.name
              ? conversationInfo.name
              : (conversationInfo?.members ?? []).join(', ')
          }}
        </span>
      </div>
    </ng-template>
  `,
  standalone: false,
})
export class ConversationMiniatureComponent {
  @Input({ required: true }) data!: any;
  @Input() miniatureFormat = true;
  @Input() options!: ConversationViewOptions[];
  @Input() showDownloadButton: boolean | null = true;
  @Input() showHighlightButton: boolean | null = true;
  @Input() translationsEnabled: boolean = false;
  isSelectedData = input(false);
  @Output() downloadConversation = new EventEmitter<any>();
  @Output() changeViewOption = new EventEmitter<string>();
  @Output() scrollToHighlight = new EventEmitter<void>();

  get conversationInfo() {
    return (
      this.data?.conversation ?? {
        type: '',
        is_channel: false,
        total: 0,
        members: [],
        name: '',
        start_date: null,
      }
    );
  }

  get isCorrupted(): boolean {
    return this.data?.corrupted === true;
  }

  toggleSelected(value: string) {
    this.changeViewOption.emit(value);
  }

  onImgError(event: Event) {
    const target = event.target as HTMLImageElement;
    if (target) {
      target.src = '/assets/images/icons/social-icons/DEFAULT.png';
    }
  }

  startDownload() {
    this.downloadConversation.emit();
  }

  handleScrollToHighlight() {
    this.scrollToHighlight.emit();
  }
}
